import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import type { InvitePreview } from '../types';

// PUBLIC page for the invite link (/accept-invite?invite=<id>). The invitee is
// pre-created in Cognito by the inviter, so here they simply set a password: we
// preview the invite, let them choose a password, POST /members/accept-password
// (sets the password + joins the team), then sign them in and drop them at the
// team page. No sign-up round-trip, no localStorage stash.
function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-card">
        {children}
      </div>
    </div>
  );
}

export function AcceptInvitePage() {
  const { t } = useTranslation('settings');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithEmail } = useAuth();
  const inviteId = params.get('invite') ?? '';

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load the invite preview on mount.
  useEffect(() => {
    let cancelled = false;
    if (!inviteId) {
      setLoading(false);
      setInvalid(true);
      return;
    }
    api
      .previewInvite(inviteId)
      .then((p) => {
        if (cancelled) return;
        if (p.status !== 'pending') setInvalid(true);
        else setPreview(p);
      })
      .catch(() => {
        if (!cancelled) setInvalid(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inviteId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview) return;
    setError('');
    if (password.length < 8) {
      setError(t('team.accept.weakPassword'));
      return;
    }
    if (password !== confirm) {
      setError(t('team.accept.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.acceptInviteWithPassword(inviteId, password);
      await signInWithEmail(result.email, password);
      navigate('/members');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      // Surface Cognito's own weak-password guidance if it slips past our check.
      setError(/password/i.test(msg) && /policy|least|character|length/i.test(msg)
        ? t('team.accept.weakPassword')
        : t('team.accept.acceptError'));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <p className="text-center text-sm text-gray-500">{t('team.accept.working')}</p>
      </Card>
    );
  }

  if (invalid || !preview) {
    return (
      <Card>
        <p className="text-center text-sm font-medium text-red-600">{t('team.accept.invalidInvite')}</p>
        <div className="mt-4 text-center">
          <Link to="/" className="text-xs font-semibold text-primary-600 hover:underline">
            {t('team.accept.goHome')}
          </Link>
        </div>
      </Card>
    );
  }

  const roleLabel = t(`team.roles.${preview.role}`, { defaultValue: preview.role });

  return (
    <Card>
      <h1 className="text-center text-lg font-bold text-gray-900">
        {preview.inviterName
          ? t('team.accept.joinTitle', { inviter: preview.inviterName })
          : t('team.accept.joinTitleFallback')}
      </h1>
      <p className="mt-2 text-center text-sm text-gray-500">
        {t('team.accept.invitedAs', { role: roleLabel })}
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700">{t('team.accept.emailLabel')}</label>
          <input
            type="email"
            value={preview.email}
            readOnly
            className="mt-1 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700">{t('team.accept.passwordLabel')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-400">{t('team.accept.passwordHint')}</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700">{t('team.accept.confirmLabel')}</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {error && <p className="text-sm font-medium text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {submitting ? t('team.accept.settingUp') : t('team.accept.setPasswordCta')}
        </button>
      </form>
    </Card>
  );
}
